import React from 'react';

import { Link } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { ServiceSegmentType } from '@mewa/types';
import {
  DataGridList,
  DownloadDefaultIcon,
  DownloadHoverIcon,
  IconTextButton,
  ResponsiveGrid,
} from '@mewa/ui';

import t from '../../../libs/translate';
import { AccordionData, DataAccordion } from './components/data-accordion';
import InlineConsultationTeaser from './components/inline-consultation-teaser';
import { ModelIntroText } from './components/model-intro-text';
import { ProductName } from './components/product-name';
import { propertiesSeparator } from './meta-data';
import { Props, TeaserLink } from './types';
import VariantsSlider from './variants-slider/variants-slider';

const DataSectionNonClothing = ({
  data,
  accordionData,
  activeVariantIndex,
  serviceTestLink,
  contactLink,
  brochuresLink,
  onBrochuresDownload,
  onSelectionChange,
  variantsCountText,
}: {
  data: Props;
  accordionData: AccordionData;
  activeVariantIndex: number;
  serviceTestLink: TeaserLink;
  contactLink: TeaserLink;
  brochuresLink: string;
  onBrochuresDownload: () => void;
  onSelectionChange: (newIndex: number) => void;
  variantsCountText: string;
}) => {
  return (
    <>
      <MobileVariantsSlider
        activeIndex={activeVariantIndex}
        data={data}
        onSelectionChange={onSelectionChange}
        variantsCountText={variantsCountText}
      />
      <ResponsiveGrid sx={{ paddingTop: { xs: '44px', md: '94px' } }}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SliderWrapper>
            <DesktopVariantsSlider
              slim
              activeIndex={activeVariantIndex}
              data={data}
              onSelectionChange={onSelectionChange}
              variantsCountText={variantsCountText}
            />
          </SliderWrapper>
        </Grid>
        <Grid
          sx={{ maxWidth: { md: '700px' } }}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <StyledProductName
            displayName={data.pimProduct.displayName}
            collectionName={data.pimProduct.collection?.name}
            modelName={data.pimProduct.modelName}
          />
          <KeyFacts
            items={data.pimProduct.keyFacts}
            data-pagefind-meta={`product_key_facts:${data.pimProduct.keyFacts.join(
              propertiesSeparator,
            )}`}
          />
          {data.pimProduct.serviceSegment.key ===
          ServiceSegmentType.cleaningCloths ? (
            <InlineConsultationTeaser
              data-pagefind-ignore
              thumbnailKey={'serviceTest'}
              description={serviceTestLink.description}
              buttonLabel={serviceTestLink.buttonLabel}
              onClick={serviceTestLink.onClick}
            />
          ) : (
            <InlineConsultationTeaser
              data-pagefind-ignore
              thumbnailKey={'consultation'}
              description={contactLink.description}
              buttonLabel={contactLink.buttonLabel}
              onClick={contactLink.onClick}
            />
          )}
          <Grid
            size={{
              xs: 12,
              sm: 10,
              md: 12,
            }}
          >
            <ModelIntroText
              headline={data.pimProduct.h1}
              body={data.pimProduct.infoText}
            />
          </Grid>

          <DataAccordion contactLink={contactLink} data={accordionData} />

          <BrochuresDownloadButtonContainer>
            <IconTextButton
              DefaultIcon={DownloadDefaultIcon}
              HoverIcon={DownloadHoverIcon}
              link={{
                component: Link,
                props: {
                  onClick: onBrochuresDownload,
                  to: brochuresLink,
                },
              }}
            >
              {t(data.translations, 'general_download_brochure')}
            </IconTextButton>
          </BrochuresDownloadButtonContainer>
        </Grid>
      </ResponsiveGrid>
    </>
  );
};

export default DataSectionNonClothing;

const MobileVariantsSlider = styled(VariantsSlider)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const DesktopVariantsSlider = styled(VariantsSlider)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
}));

const SliderWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    maxWidth: '800px',
    marginLeft: 'auto',
  },
}));

const StyledProductName = styled(ProductName)(({ theme }) => ({
  display: 'none',
  flexDirection: 'column-reverse',

  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },

  'h1, h2': {
    margin: 0,
  },

  h1: {
    ...theme.typography.h3,
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: '6px',

    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.h6.fontSize,
      lineHeight: theme.typography.h6.lineHeight,
    },
  },

  h2: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

const KeyFacts = styled(DataGridList)(({ theme }) => ({
  marginTop: '38px',
  marginBottom: '26px',

  ...theme.typography.body,

  'li:not(:last-of-type)': {
    marginBottom: '10px',
  },

  [theme.breakpoints.down('md')]: {
    marginTop: 0,
  },
}));

const BrochuresDownloadButtonContainer = styled('div')({
  marginTop: '18px',
  marginLeft: '-12px',
});
